import { S3Client } from '@aws-sdk/client-s3';
import { t } from 'i18next';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Trans } from 'react-i18next';
import { ReactComponent as Access } from '../../../assets/img/storage/actions-access.svg';
import { ReactComponent as Copy } from '../../../assets/img/storage/actions-copy.svg';
import { ReactComponent as Download } from '../../../assets/img/storage/actions-dl.svg';
import { ReactComponent as More } from '../../../assets/img/storage/actions-more.svg';
import { ReactComponent as Move } from '../../../assets/img/storage/actions-move.svg';
import { ReactComponent as Remove } from '../../../assets/img/storage/actions-remove.svg';
import { ReactComponent as Rename } from '../../../assets/img/storage/actions-rename.svg';
import { ReactComponent as Share } from '../../../assets/img/storage/actions-share.svg';
import { ReactComponent as Extract } from '../../../assets/svg/ExtractAndStay.svg';
import { ReactComponent as TransCode } from '../../../assets/svg/Tools.svg';
import { useAlert } from '../../../components/alert';
import { extractErrorDetail } from '../../../components/common';
import Button from '../../../components/mplat/Button/Button';
import Label from '../../../components/mplat/Label';
import { BaseModalProps, Modal } from '../../../components/mplat/Modal';
import Select from '../../../components/mplat/Select';
import TextInput from '../../../components/mplat/TextInput';
import { useUploadContext } from '../../../contexts/UploadContext';
import { useAuth } from '../../../hooks';
import { ReactComponent as CloseIcon } from '../../../img/storage/actions-close.svg';
import { useGetSdnDisksQuery } from '../../../services/sdn';
import { ModuleTaskStatus, useCreateWebcoderTaskMutation, useGetWebcoderProfilesQuery, useLazyGetTranscodingTaskQuery, WebcoderOutParams } from '../../../services/transcode';
import { getBasename, s3dirname, StorageFolderSelection } from './StorageMy';

interface TranscodeModalProps extends BaseModalProps {
    prefix: string,
    client: S3Client,
}

export function TranscodeModal({ isOpen, onClose, client, prefix }: TranscodeModalProps) {
    const { account } = useAuth();
    const alert = useAlert();
    const { dispatchTask } = useUploadContext();

    const [createTranscode] = useCreateWebcoderTaskMutation();
    const { data: presets } = useGetWebcoderProfilesQuery();
    const { data: sdnDisks } = useGetSdnDisksQuery();
    const [fetchTaskInfo] = useLazyGetTranscodingTaskQuery();

    const [success, setSuccess] = useState<boolean>();
    const [fail, setFail] = useState<string>();
    const [profileId, setProfileId] = useState<string>();
    const [currentProfile, setCurrentProfile] = useState<WebcoderOutParams>();
    const [path, setPath] = useState(s3dirname(prefix));
    const [filename, setFilename] = useState(getBasename(prefix) + '.transcoded');
    const [isOpenFolderSelection, setIsOpenFolderSelection] = useState(false);

    const successClass = 'popup_message';
    const bucketname = useMemo(() => sdnDisks?.results.find((disk) => disk.account.id === Number(account))?.bucket, [sdnDisks]);

    const availProfiles = useMemo(() => {
        const tmp = presets?.results.map((preset) => ({ value: preset.label, label: preset.name, params: preset.parameters.outputs })) || [];
        if (tmp[0]) setProfileId(tmp[0].value);
        return tmp;
    }, [presets]);

    useEffect(() => {
        if (profileId) {
            const tmpCurrProfile = availProfiles.find((p) => p.value === profileId)?.params?.at(0);
            setCurrentProfile(tmpCurrProfile);
            const fname = getBasename(prefix);
            const farr = fname.split('.');
            const ext = farr.length > 1 ? farr.pop() : '';
            const now = new Date();
            const yyyymmdd = now.getFullYear() +
                String(now.getMonth() + 1).padStart(2, '0') +
                String(now.getDate()).padStart(2, '0');
            const hh = String(now.getHours()).padStart(2, '0');
            const mm = String(now.getMinutes()).padStart(2, '0');
            const dd = String(now.getDate()).padStart(2, '0');
            const hhmmdd = `${hh}${mm}${dd}`;
            setFilename(`${farr.join('.')}_${profileId}_${yyyymmdd}_${hhmmdd}.${tmpCurrProfile?.filename || ext}`);
        }
    }, [profileId, availProfiles]);

    useEffect(() => {
        if (isOpen) {
            setSuccess(false);
        }
    }, [isOpen]);

    const taskWatchDog = useCallback((tid: string) => {
        fetchTaskInfo(tid).unwrap()
            .then((res) => {
                res.results.log.forEach((msg) => {
                    if (msg.level === 'ERROR') {
                        alert.error(msg.message);
                        dispatchTask({ id: tid, status: res.results.status, log: res.results.log.map((m) => m.message), type: 'deleted' });
                    }
                });
                if (res.results.status < ModuleTaskStatus.COMPLETED) {
                    setTimeout(() => taskWatchDog(tid), 2500);
                } else {
                    dispatchTask({ id: tid, status: res.results.status, log: res.results.log.map((msg) => msg.message), type: 'deleted' });
                }
            })
            .catch((e) => alert.error(extractErrorDetail(e)));
    }, [fetchTaskInfo]);

    return (
        <Modal className={success ? successClass : 'form-opinion'} isOpen={isOpen} onClose={() => onClose()}>
            {isOpenFolderSelection && <StorageFolderSelection
                client={client}
                bucketname={bucketname || ''}
                isOpen={isOpenFolderSelection}
                onClose={() => setIsOpenFolderSelection(false)}
                prefix={[s3dirname(prefix)]}
                mode={'none'}
                initPath={s3dirname(prefix)}
                onAction={(p) => {
                    setPath(p);
                    setIsOpenFolderSelection(false);
                }}
            />}
            {success ?
                <div className="form-message _success">
                    <div className="form-message__icon" />
                    <h2 className="popup__heading form-message__heading">
                        Спасибо за&nbsp;ваши предложения
                    </h2>
                    <div className="form-message__copy">
                        Мы&nbsp;обязательно примем их&nbsp;к&nbsp;рассмотрению
                    </div>
                    <div className="form-message__ok">
                        <span className="btn" onClick={() => onClose()}>Хорошо</span>
                    </div>
                </div>
                :
                ((fail || !prefix)
                    ?
                    <div className="form-message _error">
                        <div className="form-message__icon"></div>
                        <h2 className="popup__heading form-message__heading">
                            {!prefix ?
                                'Отсутствуют медиа файлы'
                                :
                                'Что-то пошло не так'
                            }
                        </h2>
                        <div className="form-message__copy">
                            {fail}
                            {!prefix &&
                                <>Для транскодирования загрузите файлы контента</>
                            }
                        </div>
                        <div className="form-message__ok">
                            <span className="btn" onClick={() => onClose()}>Понятно</span>
                        </div>
                    </div>
                    :
                    <div className="transcode-order">
                        <div className="transcode-order__heading">Заказ транскодирования</div>
                        <Label><Trans>Profile</Trans></Label>
                        <Select value={profileId} options={availProfiles} onChange={(v) => setProfileId(v)} />
                        <div className="transcode-order__settings" style={{ maxWidth: 'max-content' }}>
                            {/* <div className="transcode-order__settings-item">
                                Контейнер <strong>HLS</strong>
                            </div> */}
                            {/* <div className="transcode-order__settings-sep"></div> */}
                            <div className="transcode-order__settings-item">
                                <Trans>Video codec</Trans> <strong style={{ textTransform: 'uppercase' }}>{currentProfile?.video_codec}</strong>
                            </div>
                            <div className="transcode-order__settings-sep"></div>
                            <div className="transcode-order__settings-item">
                                Аудиокодек <strong style={{ textTransform: 'uppercase' }}>{currentProfile?.audio_codec}</strong>
                            </div>
                        </div>
                        <ul className="transcode-order__formats">
                            <li>Аудио каналы · <strong>{currentProfile?.ac}×{currentProfile?.audio_bitrate}</strong></li>
                            <li>Частота дискретизации аудио · <strong>{currentProfile?.ar}</strong></li>
                            <li>{currentProfile?.width}×{currentProfile?.height} · <strong>{currentProfile?.video_bitrate}</strong> bps</li>
                            <li>Частота кадров · <strong>{currentProfile?.frame_rate}</strong></li>
                            <li>Пресет · <strong>{currentProfile?.preset}</strong></li>
                            <li>Профиль кодирования · <strong>{currentProfile?.profile}</strong></li>
                            <li>Название результирующего файла · <strong>{currentProfile?.filename}</strong></li>
                            <li style={{ marginBottom: '6px' }}>Volume · <strong>{currentProfile?.volume}</strong></li>
                        </ul>
                        <Label><Trans>storage:SAVE_TO_FOLDER</Trans>: <strong>{path || '/'}</strong></Label>
                        <Button onClick={() => setIsOpenFolderSelection(true)}><Trans>Choose</Trans></Button>

                        <TextInput value={filename} onChange={(e) => setFilename(e.target.value)} label={t('Filename')} />

                        <div className="transcode-order__submit">
                            <span
                                className="btn"
                                onClick={() => {
                                    if (profileId) {
                                        createTranscode({ source: `${bucketname}/${prefix}`, target: `${bucketname}/${path}${filename}`, 'module-config': { preset: profileId } }).unwrap().then((res) => {
                                            const taskId = res.results.id;
                                            taskWatchDog(taskId);
                                            dispatchTask({ id: taskId, status: ModuleTaskStatus.CREATED, log: [], type: 'added' });
                                            onClose();
                                        }).catch(e => {
                                            setFail(extractErrorDetail(e));
                                        });
                                    }
                                }}
                            >
                                Подтвердить заказ
                            </span>
                        </div>
                    </div>
                )
            }
        </Modal>
    );
}


interface StorageActionsProps {
    selected: string[],
    onClean: () => void,
    readonly?: boolean
    base?: boolean
    forbidDelete?: boolean,
    client: S3Client,
    onDownloadZip?: (v: string) => void
    onDownload?: (v: string[]) => void,
    onShare?: (v: string) => void
    onAccess?: (v: string) => void
    onRename?: (v: string) => void
    onCopy?: (v: string[]) => void
    onCopyInMy?: (v: string[]) => void
    onMove?: (v: string[]) => void
    onDelete?: (v: string[]) => void
    onUnzip?: (v: string) => void
    onZip?: (v: string[]) => void
}

function StorageActions({
    selected, readonly, base, forbidDelete, onClean, client,
    onAccess, onCopy, onCopyInMy, onDelete, onDownload, onMove, onShare, onRename, onUnzip, onZip, onDownloadZip,
}: StorageActionsProps) {
    const single = selected.length === 1;
    const isFolder = single && selected[0].endsWith('/');
    const haveFolder = selected.some((s) => s.endsWith('/'));
    const isZip = single && selected[0].endsWith('.zip');

    const [showTranscodeModal, setShowTranscodeModal] = useState(false);

    if (forbidDelete && isFolder && !onDownloadZip && !onCopyInMy) return null;

    return (
        <div className={`storage__file-actions ${selected.length > 1 ? '_multiple' : ''} _archive`}>
            <TranscodeModal
                client={client}
                prefix={selected[0]}
                isOpen={showTranscodeModal}
                onClose={() => setShowTranscodeModal(false)}
            />

            {!(readonly || base) && single && !isFolder && <div className="storage__file-actions-group _main">
                <div className="storage__file-actions-item _download" onClick={() => { setShowTranscodeModal(true); }}>
                    <TransCode />
                    <span className="storage__file-actions-item-label">
                        <Trans>Transcode</Trans>
                    </span>
                </div>
                {isFolder && onDownloadZip && <div className="storage__file-actions-item _download" onClick={() => { onDownloadZip(selected[0]); onClean(); }}>
                    <Download />
                    <span className="storage__file-actions-item-label">
                        <Trans>Download</Trans>
                    </span>
                </div>}
                {!isFolder && onDownload && <div className="storage__file-actions-item _download" onClick={() => { onDownload(selected); onClean(); }}>
                    <Download />
                    <span className="storage__file-actions-item-label">
                        <Trans>Download</Trans>
                    </span>
                </div>}
                {!(readonly || base) && <>
                    {onShare && <div className="storage__file-actions-item _share" onClick={() => { onShare(selected[0]); onClean(); }}>
                        <Share />
                        <span className="storage__file-actions-item-label">
                            <Trans>storage:SHARE</Trans>
                        </span>
                    </div>}
                    {onAccess && <div className="storage__file-actions-item _access" onClick={() => { onAccess(selected[0]); onClean(); }}>
                        <Access />
                        <span className="storage__file-actions-item-label">
                            <Trans>Access</Trans>
                        </span>
                    </div>}
                </>}
            </div>
            }
            <div className="storage__file-actions-group _storage">
                <div className="storage__file-actions-item _counter" onClick={() => onClean()}>
                    <span className="storage__file-actions-item-label">{selected.length}</span>
                </div>
                {!single && !haveFolder && onDownload && <div className="storage__file-actions-item _download" onClick={() => { onDownload(selected); onClean(); }}>
                    <Download />
                    <span className="storage__file-actions-item-label">
                        <Trans>Download</Trans>
                    </span>
                </div>}
                {onZip && (
                    <div className="storage__file-actions-item _unpack-save" onClick={() => { onZip(selected); onClean(); }}>
                        <Extract />
                        <span className="storage__file-actions-item-label">
                            <Trans>storage:ZIP</Trans>
                        </span>
                    </div>
                )}
                {isZip && onUnzip && (
                    <div className="storage__file-actions-item _unpack-save" onClick={() => { onUnzip(selected[0]); onClean(); }}>
                        <Extract />
                        <span className="storage__file-actions-item-label">
                            <Trans>storage:EXTRACT</Trans>
                        </span>
                    </div>
                )}
                {onCopyInMy && (
                    <div className="storage__file-actions-item _copy" onClick={() => { onCopyInMy(selected); onClean(); }}>
                        <Copy />
                        <span className="storage__file-actions-item-label">
                            <Trans>storage:COPY_TO_MY</Trans>
                        </span>
                    </div>
                )}
                {!(readonly || forbidDelete) && <>
                    {onCopy && <div className="storage__file-actions-item _copy" onClick={() => { onCopy(selected); onClean(); }}>
                        <Copy />
                        <span className="storage__file-actions-item-label">
                            <Trans>storage:COPY_TO</Trans>
                        </span>
                    </div>}
                    {onMove && <div className="storage__file-actions-item _move" onClick={() => { onMove && onMove(selected); onClean(); }}>
                        <Move />
                        <span className="storage__file-actions-item-label">
                            <Trans>storage:MOVE</Trans>
                        </span>
                    </div>}
                    {onRename && <div className="storage__file-actions-item _rename" onClick={() => { onRename(selected[0]); onClean(); }}>
                        <Rename />
                        <span className="storage__file-actions-item-label">
                            <Trans>storage:RENAME</Trans>
                        </span>
                    </div>}
                </>}
                {!forbidDelete && onDelete && <div className="storage__file-actions-item _remove" onClick={() => { onDelete(selected); onClean(); }}>
                    <Remove />
                    <span className="storage__file-actions-item-label">
                        <Trans>Delete</Trans>
                    </span>
                </div>}
                {!(readonly || forbidDelete) && <>
                    <div className="storage__file-actions-item _more">
                        <More />
                        <span className="storage__file-actions-item-label">
                            <Trans>storage:YET</Trans>
                        </span>
                        <div className="storage__file-actions-item-list">
                            {onCopy && <div className="storage__file-actions-item _copy" onClick={() => { onCopy(selected); onClean(); }}>
                                <Copy />
                                <span className="storage__file-actions-item-label">
                                    <Trans>storage:COPY_TO</Trans>
                                </span>
                            </div>}
                            {onMove && <div className="storage__file-actions-item _move" onClick={() => { onMove(selected); onClean(); }}>
                                <Move />
                                <span className="storage__file-actions-item-label">
                                    <Trans>storage:MOVE</Trans>
                                </span>
                            </div>}
                            {onRename && <div className="storage__file-actions-item _rename" onClick={() => { onRename(selected[0]); onClean(); }} >
                                <Rename />
                                <span className="storage__file-actions-item-label">
                                    <Trans>storage:RENAME</Trans>
                                </span>
                            </div>}
                        </div>
                    </div>
                </>}
                <div className="storage__file-actions-item _close" onClick={() => onClean()}>
                    <CloseIcon />
                    <span className="storage__file-actions-item-label">
                        <Trans>Close</Trans>
                    </span>
                </div>
            </div>
        </div>
    )
}

export default StorageActions;
